// Copyright (c) StrangeBee 2024

import React, { ReactElement, useMemo } from 'react';
import { DescribeFieldOutput, ExportEntitiesType, QueryParams } from 'thehive-sdk';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { ExportForm } from '@Organisms/Export/ExportDrawer/ExportForm';
import client from '@Client';
import { computeEventList, ExportEventList } from '@Organisms/Export/eventList';

interface ExportManageProps {
    objectType: ExportEntitiesType;
    selectedItems: string[];
    triggerElem?: ReactElement;
    objectData?: Record<string, string>;
    describeField?: DescribeFieldOutput;
    visible?: boolean;
    afterVisible?: (visible: boolean) => void;
    onVisibleChange?: (visible: boolean) => void;
    queryParams?: QueryParams;
    listNumber?: number;
}

const ExportManage = ({
    objectType,
    triggerElem,
    selectedItems,
    visible,
    afterVisible,
    onVisibleChange,
    queryParams,
    listNumber = 0,
}: ExportManageProps): ReactElement => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const number = selectedItems.length ? selectedItems.length : listNumber;

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.ExportController,
                locale: i,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                },
                onError: handleNotificationError,
            }),
        [i],
    );

    return (
        <THDrawer
            title={i(`export.drawer.title.${objectType.toLowerCase()}.${number > 1 ? 'multiple' : 'single'}`)}
            triggerElem={triggerElem}
            width="35vw"
            afterVisibleChange={(visible) => {
                form.resetFields();
                setFieldsTouched(false);
                onVisibleChange?.(visible);
                afterVisible?.(visible);
            }}
            visible={!!visible}
            className="drawer-form"
            disableClose={false}
        >
            {(toggleDrawer) => (
                <ExportForm
                    isValid={isValid && fieldsTouched}
                    onFinish={(values) => {
                        eventList[objectType as keyof ExportEventList].onExport(values, selectedItems, queryParams);
                        toggleDrawer();
                    }}
                    onCancel={() => {
                        toggleDrawer();
                        form.resetFields();
                        setFieldsTouched(false);
                    }}
                    form={form}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    number={number}
                />
            )}
        </THDrawer>
    );
};

export default ExportManage;
